import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Phone, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

/**
 * Phone OTP verification dialog (mocked provider in V1).
 *
 * Flow:
 *   1. User types phone + country code, hits "Send code".
 *   2. We POST /users/me/phone/start, get back {request_id, code}.
 *   3. Because the provider is the MockPhoneVerification, the API returns the
 *      code in the response — we surface it via toast tagged MOCK so the
 *      developer can finish the flow without real SMS.
 *   4. User types code, we POST /users/me/phone/verify, on success the
 *      user.phone_verified flag flips and parent re-renders.
 */
const PhoneVerifyDialog = ({ open, onOpenChange, initialPhone = '', initialCode = '+1', onVerified }) => {
  const { api } = useAuth();
  const [phone, setPhone] = useState(initialPhone);
  const [code, setCode] = useState(initialCode);
  const [otp, setOtp] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [stage, setStage] = useState('enter_phone'); // enter_phone | enter_code
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Reset state every time dialog opens fresh
  useEffect(() => {
    if (open) {
      setPhone(initialPhone || '');
      setCode(initialCode || '+1');
      setOtp('');
      setRequestId(null);
      setStage('enter_phone');
      setError('');
    }
  }, [open, initialPhone, initialCode]);

  const sendCode = async (e) => {
    e?.preventDefault();
    setError('');
    if (!/^\+\d{1,4}$/.test(code)) {
      setError("Country code should look like +1 or +44.");
      return;
    }
    if (!/^\d{6,15}$/.test(phone.replace(/\D/g, ''))) {
      setError("Hmm, that doesn't look like a valid phone number.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/users/me/phone/start', {
        phone: phone.replace(/\D/g, ''),
        phone_country_code: code,
      });
      setRequestId(res.data.request_id);
      setStage('enter_code');
      if (res.data.mocked && res.data.code) {
        toast(`Mock OTP code: ${res.data.code}`, {
          description: 'Real SMS not sent. Type this code below.',
          duration: 12000,
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send the code. Try again?');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(otp)) {
      setError('The code is 6 digits.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/users/me/phone/verify', {
        request_id: requestId,
        code: otp,
      });
      toast.success('Phone verified.');
      onVerified?.(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.detail || "That code didn't match. Try again or send a new one.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100%-2rem)] sm:max-w-md bg-card-cream border-0 shadow-xl p-5 sm:p-6"
        data-testid="phone-verify-dialog"
      >
        <DialogHeader className="text-center">
          <div className="mx-auto w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-3">
            {stage === 'enter_phone' ? (
              <Phone className="h-7 w-7 text-amber-600" />
            ) : (
              <ShieldCheck className="h-7 w-7 text-amber-600" />
            )}
          </div>
          <DialogTitle className="text-lg sm:text-xl font-heading font-bold text-slate-900">
            {stage === 'enter_phone' ? 'Verify your phone' : 'Enter the 6-digit code'}
          </DialogTitle>
          <DialogDescription className="text-slate-600 mt-2 leading-relaxed">
            {stage === 'enter_phone'
              ? "We'll send a 6-digit code. Verifying your phone bumps your Trust Score by 5 points."
              : `Code sent to ${code} ${phone}.`}
          </DialogDescription>
        </DialogHeader>

        {stage === 'enter_phone' ? (
          <form onSubmit={sendCode} className="mt-4 space-y-3" data-testid="phone-start-form">
            <div className="flex gap-2">
              <div className="w-24">
                <Label htmlFor="ph-cc" className="text-xs font-semibold text-slate-700">
                  Code
                </Label>
                <Input
                  id="ph-cc"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="h-12 bg-white"
                  data-testid="phone-country-code"
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="ph-num" className="text-xs font-semibold text-slate-700">
                  Phone number
                </Label>
                <Input
                  id="ph-num"
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="416 555 1212"
                  className="h-12 bg-white"
                  data-testid="phone-number-input"
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button
              type="submit"
              disabled={busy}
              className="w-full btn-primary py-6 text-base"
              data-testid="phone-send-code-btn"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Send code'}
            </Button>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="mt-4 space-y-3" data-testid="phone-verify-form">
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              className="h-14 bg-white text-center text-2xl tracking-[0.5em] font-mono"
              data-testid="phone-otp-input"
              autoFocus
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button
              type="submit"
              disabled={busy}
              className="w-full btn-primary py-6 text-base"
              data-testid="phone-verify-btn"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verify'}
            </Button>
            <button
              type="button"
              onClick={() => setStage('enter_phone')}
              className="w-full text-sm text-slate-600 hover:text-slate-900 py-2"
              data-testid="phone-resend-link"
            >
              Wrong number or didn't get it? Send again.
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhoneVerifyDialog;
